import React, { useState } from 'react';
import { Download, Link2, Paperclip } from 'lucide-react';

/**
 * A message's attachments, each downloadable.
 *
 * The images drawn into the body (its `cid:` references) are not listed: the
 * service writes those into the body itself and leaves them out of
 * `message.attachments`, which is how an email client shows them.
 *
 * A download is fetched with the session and handed to the browser as bytes
 * (`application/octet-stream`), never opened: an attachment is whatever the
 * sender sent, and an HTML file opened from a blob URL would run as this page.
 */

export const fmtSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// The request asks for a Blob, so an error's JSON detail arrives as one too;
// the status says enough.
export const downloadError = (err, name) => {
  const status = err?.response?.status;
  if (status === 413) return `${name} is too large to download here.`;
  if (status === 404) return `${name} was not found. The message may have moved; reopen it.`;
  return `Could not download ${name}.`;
};

const saveBytes = (data, name) => {
  const url = URL.createObjectURL(new Blob([data], { type: 'application/octet-stream' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name || 'attachment';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const Attachments = ({ api, message }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const items = message.attachments || [];
  if (!items.length) return null;

  const download = async (a) => {
    setBusy(a.id);
    setError(null);
    try {
      saveBytes(await api.getAttachment(message.id, a.id), a.name);
    } catch (err) {
      setError(downloadError(err, a.name || 'The attachment'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3">
      <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <Paperclip className="h-3.5 w-3.5" />
        {items.length === 1 ? '1 attachment' : `${items.length} attachments`}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((a) =>
          a.kind === 'link' ? (
            <span
              key={a.id}
              title="A link to a file stored elsewhere; open it from Outlook"
              className="inline-flex max-w-full items-center gap-1.5 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-500"
            >
              <Link2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{a.name || 'Linked file'}</span>
            </span>
          ) : (
            <button
              key={a.id}
              type="button"
              onClick={() => download(a)}
              disabled={busy === a.id}
              title={`Download ${a.name}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1 text-sm text-gray-800 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-60"
            >
              <Download className={`h-3.5 w-3.5 shrink-0 ${busy === a.id ? 'animate-pulse' : ''}`} />
              <span className="truncate">{a.name || 'attachment'}</span>
              {a.size > 0 && <span className="shrink-0 text-xs text-gray-500">{fmtSize(a.size)}</span>}
            </button>
          )
        )}
      </div>
      {error && <div className="mt-1 text-xs text-red-700">{error}</div>}
    </div>
  );
};

export default Attachments;
